import React, { useEffect, useState } from 'react';
import { Block } from '../classes/Block';
import { t } from '@lingui/core/macro';
import './BlockCreationPopup.css';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';

export type BlockCreationRequest =
  | {
      type: 'empty';
      createSbsPair: boolean;
    }
  | {
      type: 'clone';
      sourceBlockId: string;
      createSbsPair: boolean;
    };

interface BlockCreationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (request: BlockCreationRequest) => void;
  existingBlocks: Block[];
  hasSbsBlocks: boolean;
  sbsCloneSourceBlockIds: string[];
}

const BlockCreationPopup: React.FC<BlockCreationPopupProps> = ({
  isOpen,
  onClose,
  onCreate,
  existingBlocks,
  hasSbsBlocks,
  sbsCloneSourceBlockIds,
}) => {
  const [selectedOption, setSelectedOption] = useState<'empty' | 'clone'>('empty');
  const [selectedBlockId, setSelectedBlockId] = useState<string>(existingBlocks[0]?.id ?? '');
  const [createSbsPair, setCreateSbsPair] = useState<boolean>(hasSbsBlocks);
  const selectedCloneSupportsSbsPair: boolean = sbsCloneSourceBlockIds.includes(selectedBlockId);
  const shouldOfferSbsPair: boolean = selectedOption === 'empty'
    ? hasSbsBlocks
    : selectedCloneSupportsSbsPair;
  const sbsOfferKey: string = `${selectedOption}:${selectedBlockId}:${shouldOfferSbsPair ? 'with-sbs' : 'plain'}`;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelectedOption('empty');
    setSelectedBlockId(existingBlocks[0]?.id ?? '');
    setCreateSbsPair(hasSbsBlocks);
  }, [existingBlocks, hasSbsBlocks, isOpen, sbsCloneSourceBlockIds]);

  useEffect(() => {
    if (!existingBlocks.some((block: Block) => block.id === selectedBlockId)) {
      setSelectedBlockId(existingBlocks[0]?.id ?? '');
    }
  }, [existingBlocks, selectedBlockId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setCreateSbsPair(shouldOfferSbsPair);
  }, [isOpen, sbsOfferKey, shouldOfferSbsPair]);

  if (!isOpen) return null;

  const handleCreate = () => {
    if (selectedOption === 'empty') {
      onCreate({
        type: 'empty',
        createSbsPair,
      });
    } else if (selectedOption === 'clone' && selectedBlockId) {
      onCreate({
        type: 'clone',
        sourceBlockId: selectedBlockId,
        createSbsPair: shouldOfferSbsPair ? createSbsPair : false,
      });
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleCancel} className="block-creation-dialog" aria-labelledby="block-creation-title" maxWidth={false}>
      <DialogTitle id="block-creation-title">{t`Create New Block`}</DialogTitle>
      <DialogContent className="block-creation-popup">

        <div className="creation-options">
          <label className="option-label">
            <input
              type="radio"
              autoFocus
              name="creationType"
              value="empty"
              checked={selectedOption === 'empty'}
              onChange={(e) => setSelectedOption(e.target.value as 'empty' | 'clone')}
            />
            <span className="option-text">{t`Empty Block`}</span>
          </label>

          <label className="option-label">
            <input
              type="radio"
              name="creationType"
              value="clone"
              checked={selectedOption === 'clone'}
              onChange={(e) => setSelectedOption(e.target.value as 'empty' | 'clone')}
              disabled={existingBlocks.length === 0}
            />
            <span className="option-text">{t`Clone from Existing`}</span>
          </label>
        </div>

        {selectedOption === 'empty' && shouldOfferSbsPair && (
          <label className="sbs-option-label">
            <input
              type="checkbox"
              checked={createSbsPair}
              onChange={(e) => setCreateSbsPair(e.target.checked)}
            />
            <span>{t`Create side-by-side pair`}</span>
          </label>
        )}

        {selectedOption === 'clone' && existingBlocks.length > 0 && (
          <div className="block-selection">
            <label htmlFor="blockSelect">{t`Select block to clone:`}</label>
            <select
              id="blockSelect"
              value={selectedBlockId}
              onChange={(e) => setSelectedBlockId(e.target.value)}
              className="block-select"
            >
              {existingBlocks.map((block) => (
                <option key={block.id} value={block.id}>
                  {block.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedOption === 'clone' && shouldOfferSbsPair && (
          <label className="sbs-option-label">
            <input
              type="checkbox"
              checked={createSbsPair}
              onChange={(e) => setCreateSbsPair(e.target.checked)}
            />
            <span>{t`Create side-by-side pair`}</span>
          </label>
        )}

        <div className="popup-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="cancel-btn"
          >
            {t`Cancel`}
          </button>
          <button
            type="button"
            onClick={handleCreate}
            className="create-btn"
            disabled={selectedOption === 'clone' && !selectedBlockId}
          >
            {t`Create`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BlockCreationPopup; 
